"use client";

import {
  Calendar,
  CreditCard,
  MapPin,
  Shield,
  Users,
  Zap,
} from "lucide-react";
import { useTranslation } from "react-i18next";

const featureKeys = [
  { key: "easyBooking", icon: Calendar },
  { key: "instantConfirmation", icon: Zap },
  { key: "securePayments", icon: CreditCard },
  { key: "reliableService", icon: Shield },
  { key: "communityFirst", icon: Users },
  { key: "venuesEverywhere", icon: MapPin },
] as const;

export default function AboutFeaturesGrid() {
  const { t } = useTranslation();

  return (
    <section className="relative overflow-hidden border-y border-border-soft bg-gradient-to-b from-slate-50/90 via-surface-muted to-slate-50/80 py-14 sm:py-16 md:py-24 lg:py-28">
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.4]"
        style={{
          backgroundImage: `radial-gradient(circle at 20% 20%, rgb(34 72 214 / 0.06), transparent 45%),
            radial-gradient(circle at 80% 80%, rgb(34 72 214 / 0.05), transparent 40%)`,
        }}
        aria-hidden
      />
      <div className="relative mx-auto w-full max-w-6xl px-4 sm:px-6">
        <header className="mx-auto max-w-2xl text-center">
          <span className="inline-block rounded-full bg-white px-3 py-1.5 text-xs font-semibold text-primary shadow-sm ring-1 ring-primary/10 sm:px-4 sm:text-sm">
            {t("about.whyChooseUs")}
          </span>
          <h2 className="mt-4 text-balance text-xl font-bold uppercase tracking-tight text-slate-900 sm:mt-5 sm:text-2xl md:text-3xl lg:text-4xl">
            {t("about.advantage")}
          </h2>
        </header>

        {/* items-start: card height follows text; no stretched empty space */}
        <ul className="mt-10 grid list-none grid-cols-1 gap-4 sm:mt-12 sm:grid-cols-2 sm:gap-5 sm:items-start lg:mt-14 lg:grid-cols-3 lg:gap-6">
          {featureKeys.map(({ key, icon: Icon }) => (
            <li key={key}>
              <article className="group flex flex-col rounded-2xl border border-border-soft bg-white/95 p-5 shadow-[0_1px_3px_rgba(15,23,42,0.06)] backdrop-blur-sm transition-all duration-300 hover:border-primary/25 hover:shadow-[0_8px_30px_-12px_rgba(34,72,214,0.18)] sm:p-6">
                <div className="flex items-start gap-4">
                  <span
                    className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary-soft text-primary shadow-sm ring-1 ring-primary/15 transition-transform duration-300 group-hover:scale-[1.04]"
                    aria-hidden
                  >
                    <Icon className="h-5 w-5" strokeWidth={2} />
                  </span>
                  <div className="min-w-0 flex-1 pt-0.5">
                    <h3 className="text-base font-semibold leading-snug tracking-tight text-slate-900 sm:text-lg">
                      {t(`about.${key}`)}
                    </h3>
                    <p className="mt-2 text-pretty text-sm leading-relaxed text-slate-600 sm:mt-2.5">
                      {t(`about.${key}Desc`)}
                    </p>
                  </div>
                </div>
              </article>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
